import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useParams } from 'react-router-dom';
import { H1, H2, Text } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import { generateReportPDF } from '../utils/pdfGenerator';
import { translations } from '../translations';

const ScanResult = () => {
  const { lang = 'nl' } = useParams();
  const t = translations[lang]?.scanResult || translations.nl.scanResult;
  const location = useLocation();
  const answers = location.state?.answers || {};
  const passedScore = location.state?.score;
  const passedAnalysis = location.state?.analysis;

  const [isPaid, setIsPaid] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const analysis = useMemo(() => {
    if (passedAnalysis && typeof passedAnalysis.score === 'number') {
      return passedAnalysis;
    }
    let riskScore = passedScore ?? 20;
    const risks = [];
    let partnerProt = 80;
    let childProt = 85;
    let assetProt = 75;

    if (answers.residency && answers.residency !== 'Nederland') {
      riskScore += 35;
      partnerProt -= 45;
      risks.push({
        title: lang === 'nl' ? 'Jurisdictioneel Conflict-risico' : 'Jurisdictional Conflict Risk',
        desc: lang === 'nl' ? 'Uw internationale status vereist een expliciete rechtskeuze.' : 'Your international status requires an explicit choice of law.',
        icon: 'Globe'
      });
    }

    return {
      score: Math.min(riskScore, 100),
      confidence: 94,
      protection: { partner: partnerProt, children: childProt, assets: assetProt },
      insights: {
        risk: risks[0]?.title || (lang === 'nl' ? 'Wettelijke verdeling actief' : 'Statutory distribution active'),
        vulnerability: lang === 'nl' ? 'Fiscale optimalisatie' : 'Tax Optimization',
        opportunity: lang === 'nl' ? 'Strategisch Testament' : 'Strategic Estate Plan'
      },
      status: riskScore > 60 ? (lang === 'nl' ? 'CRITIEK' : 'CRITICAL') : (lang === 'nl' ? 'AANDACHT VEREIST' : 'ATTENTION REQUIRED')
    };
  }, [answers, passedScore, passedAnalysis, lang]);

  const handlePayment = () => {
    setIsPaid(true);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const handleDownloadPDF = async () => {
    if (!isPaid) return;
    setIsGenerating(true);
    try {
      await generateReportPDF({
        title: lang === 'nl' ? 'Strategisch Dossier' : 'Strategic Dossier',
        date: new Date().toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-US'),
        analysis,
        userData: answers
      });
    } finally {
      setTimeout(() => setIsGenerating(false), 800);
    }
  };

  const displayScore = analysis?.score ?? 0;

  return (
    <div className="min-h-screen bg-slate-50/50 pb-32">
      <div className="bg-white border-b border-slate-100 pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-navy-50 text-navy-600 text-[10px] font-bold uppercase tracking-widest mb-6 border border-navy-100">
                <SafeIcon name="Shield" /> <span>{lang === 'nl' ? 'Gevalideerde Analyse v4.2' : 'Validated Analysis v4.2'}</span>
              </div>
              <H1 className="text-navy-950 text-4xl md:text-5xl mb-4 tracking-tight">
                {t.scoreTitle} <span className="text-slate-400">{lang === 'nl' ? 'Rapport' : 'Report'}</span>
              </H1>
              <Text className="text-slate-500 text-lg max-w-xl">
                {t.scoreSubtitle}
              </Text>
            </div>
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-premium min-w-[240px] text-center relative overflow-hidden group">
              <div className="relative z-10">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t.complexity}</div>
                <div className="text-6xl font-black text-navy-950 flex items-baseline justify-center">
                  {displayScore} <span className="text-xl text-slate-300 ml-1">/100</span>
                </div>
                <div className={`mt-4 text-[9px] font-black px-4 py-1.5 rounded-full inline-block tracking-[0.1em] border ${displayScore > 60 ? 'bg-red-50 text-red-600 border-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                  {analysis.status}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-12">
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{t.findings}</h3>
                <div className="h-px bg-slate-100 flex-grow ml-4" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Card className="p-8 border-slate-100 bg-white shadow-sm" hover={true}>
                  <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center mb-6">
                    <SafeIcon name="AlertTriangle" />
                  </div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t.risk}</div>
                  <div className="text-sm font-bold text-navy-950 leading-tight mb-2">{analysis?.insights?.risk}</div>
                </Card>
                <Card className="p-8 border-slate-100 bg-white shadow-sm" hover={true}>
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center mb-6">
                    <SafeIcon name="TrendingUp" />
                  </div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t.optimization}</div>
                  <div className="text-sm font-bold text-navy-950 leading-tight mb-2">{analysis?.insights?.opportunity}</div>
                </Card>
              </div>
            </section>

            <AnimatePresence>
              {!isPaid && (
                <motion.section initial={{ opacity: 1 }} exit={{ opacity: 0, height: 0, marginBottom: 0 }} className="relative overflow-hidden">
                  <Card className="p-12 border-none shadow-premium-hover bg-white relative z-20 overflow-hidden text-center" hover={false}>
                    <div className="absolute top-0 inset-x-0 h-1 bg-navy-900" />
                    <div className="max-w-xl mx-auto space-y-8">
                      <div className="w-16 h-16 bg-navy-50 rounded-2xl flex items-center justify-center mx-auto text-navy-900 mb-4">
                        <SafeIcon name="Lock" className="text-2xl" />
                      </div>
                      <H2 className="text-2xl md:text-3xl text-navy-950 tracking-tight">{t.premiumTitle}</H2>
                      <Text className="text-slate-500 text-sm leading-relaxed">{t.premiumDesc}</Text>
                      <div className="pt-4">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{t.investment}</div>
                        <Button onClick={handlePayment} className="w-full sm:w-auto px-12 py-5 text-base shadow-xl shadow-navy-900/10">
                          {lang === 'nl' ? 'Ontgrendel voor €29' : 'Unlock for €29'}
                        </Button>
                        <div className="mt-8 text-[9px] font-bold uppercase tracking-widest opacity-40">{t.unlockTest}</div>
                      </div>
                    </div>
                  </Card>
                </motion.section>
              )}
            </AnimatePresence>

            {isPaid && (
              <motion.section initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
                <Card className="p-10 border-slate-100 shadow-premium bg-white" hover={false}>
                  <div className="flex items-center justify-between mb-12">
                    <div>
                      <H2 className="text-2xl text-navy-950">{lang === 'nl' ? 'Beschermingsanalyse' : 'Protection Analysis'}</H2>
                    </div>
                    <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border border-emerald-100">
                      {t.unlocked}
                    </div>
                  </div>
                  <div className="space-y-10">
                    {[
                      { label: lang === 'nl' ? 'Partnerbescherming' : 'Partner Protection', val: analysis?.protection?.partner ?? 80 },
                      { label: lang === 'nl' ? 'Kinderbescherming' : 'Child Protection', val: analysis?.protection?.children ?? 85 },
                      { label: lang === 'nl' ? 'Fiscaal Vermogensbehoud' : 'Tax Efficiency', val: analysis?.protection?.assets ?? 75 }
                    ].map((p, i) => (
                      <div key={i} className="space-y-3">
                        <div className="flex justify-between items-end">
                          <div className="text-sm font-bold text-navy-900">{p.label}</div>
                          <div className="text-lg font-bold text-navy-950">{p.val}%</div>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${p.val}%` }} transition={{ duration: 1.5 }} className="h-full bg-navy-900" />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.section>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-8">
            <Card className={`relative overflow-hidden border-none transition-all duration-700 ${isPaid ? 'shadow-2xl' : 'opacity-60 grayscale'}`} hover={false}>
              <div className="relative z-10 p-10 flex flex-col h-full">
                <div className="flex items-center justify-between mb-8">
                  <div className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-[0.2em] border ${isPaid ? 'bg-navy-900 text-white border-navy-900' : 'bg-slate-200 text-slate-400 border-slate-300'}`}>
                    Premium Dossier
                  </div>
                  <SafeIcon name={isPaid ? "Unlock" : "Lock"} className={isPaid ? "text-emerald-500" : "text-slate-300"} />
                </div>
                <h3 className={`text-xl font-bold tracking-tight mb-4 leading-snug ${isPaid ? 'text-navy-950' : 'text-slate-400'}`}>
                  {lang === 'nl' ? 'Strategisch Nalatenschapsdossier' : 'Strategic Estate Dossier'}
                </h3>
                <div className="mt-auto pt-6 border-t border-slate-100">
                  <Button variant="navy" className={`w-full py-5 text-xs uppercase tracking-[0.2em] shadow-xl ${!isPaid && 'bg-slate-200 border-slate-200 cursor-not-allowed text-slate-400'}`} onClick={handleDownloadPDF} disabled={!isPaid || isGenerating}>
                    {isGenerating ? (lang === 'nl' ? 'Genereren...' : 'Generating...') : isPaid ? t.downloadCta : (lang === 'nl' ? 'Vergrendeld' : 'Locked')}
                  </Button>
                </div>
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ScanResult;